import { useState, useMemo } from 'react';
import { useGFAction } from './useGFAction';
import useDeepCompareEffect from 'use-deep-compare-effect';

interface SingleFieldOptions {
	formId: number;
	fieldId: number;
}

interface MultipleFieldsOptions {
	formId: number;
	fieldIds: number[];
}

/**
 * Hook to track the visibility state of a single form field based on Gravity Forms conditional logic.
 */
export function useConditionalLogic(options: SingleFieldOptions): boolean;

/**
 * Hook to track the visibility state of multiple form fields based on Gravity Forms conditional logic.
 */
export function useConditionalLogic(options: MultipleFieldsOptions): {
	[fieldId: number]: boolean;
};

export function useConditionalLogic(
	options: SingleFieldOptions | MultipleFieldsOptions
): boolean | { [fieldId: number]: boolean } {
	const { formId } = options;

	// Normalize to always work with an array of field IDs
	const fieldIds = useMemo(
		() => ('fieldId' in options ? [options.fieldId] : options.fieldIds),
		[options]
	);

	const isSingleField = 'fieldId' in options;

	// Always use object state for consistency
	const [fieldVisibility, setFieldVisibility] = useState<{
		[fieldId: number]: boolean;
	}>({});

	// Initialize visibility state for all fields
	useDeepCompareEffect(() => {
		const initialVisibility: { [fieldId: number]: boolean } = {};

		fieldIds.forEach((fieldId) => {
			const fieldElement = document.querySelector(
				`#field_${formId}_${fieldId}`
			);
			const conditionalLogicState = fieldElement?.getAttribute(
				'data-conditional-logic'
			);
			initialVisibility[fieldId] = conditionalLogicState !== 'hidden';
		});

		setFieldVisibility(initialVisibility);
	}, [formId, fieldIds]);

	// Listen to conditional logic events for all fields
	useGFAction({
		actionName: 'gform_post_conditional_logic_field_action',
		callback: (eventFormId: number, action: string, targetId: string) => {
			// Check if this event is for one of our tracked fields
			const fieldIdMatch = targetId.match(/#field_\d+_(\d+)/);
			if (!fieldIdMatch) {
				return;
			}

			const fieldId = parseInt(fieldIdMatch[1], 10);
			if (fieldIds.includes(fieldId)) {
				setFieldVisibility((prev) => ({
					...prev,
					[fieldId]: action === 'show',
				}));
			}
		},
	});

	// Return single boolean for single field, object for multiple fields
	if (isSingleField) {
		return fieldVisibility[fieldIds[0]] ?? true;
	}

	return fieldVisibility;
}
